/**
 * 셸 원격 계측(S5 계약 — POST /api/telemetry {events:[…]} ≤50개 → 202).
 * 가명(세션 pid·course 는 서버가 Bearer 로 안다)만 · 자유 텍스트 0: code·detail 은 키 문자열(≤40자, [A-Za-z0-9._:-])만,
 * 값은 수 하나. 모양이 어긋난 사건은 버린다(보내지 않는다).
 *
 * 사건 종류(t): fe(학생·교사에게 보인 FE 코드) · perf(fps p5 · 틱 p95) · playback(입력 재생 → 위치 트랙 폴백)
 *   · break-ack(휴식 안내 확인 — SR-SEC-05 '확인 이벤트 기록') · pico(피코 반응 표시 — SR-ACH-03).
 * 묶음: 50개가 차면 · 주기(기본 20초) · 탭이 가려질 때(visibilitychange hidden · pagehide) 보낸다. 보내기 실패는 다시 큐에(최대 200).
 */
export type TelemetryKind = "fe" | "perf" | "playback" | "break-ack" | "pico";

export interface TelemetryEvent {
  t: TelemetryKind;
  /** ISO 시각 */
  at: string;
  code?: string;
  value?: number;
  detail?: string;
}

export type TelemetryInput = Omit<TelemetryEvent, "at"> & { at?: string };

export const MAX_BATCH = 50;
export const MAX_QUEUE = 200;
export const FLUSH_MS = 20_000;

const KEY = /^[A-Za-z0-9._:-]{1,40}$/;
const KINDS = new Set<TelemetryKind>(["fe", "perf", "playback", "break-ack", "pico"]);

/** 계약 모양 검사 — 자유 텍스트가 섞일 수 없게 키 모양만 통과시킨다. 통과 못 하면 null */
export function sanitize(e: TelemetryInput, now: () => number = Date.now): TelemetryEvent | null {
  if (!KINDS.has(e.t)) return null;
  const out: TelemetryEvent = { t: e.t, at: e.at ?? new Date(now()).toISOString() };
  if (e.code != null) {
    if (!KEY.test(e.code)) return null;
    out.code = e.code;
  }
  if (e.detail != null) {
    if (!KEY.test(e.detail)) return null;
    out.detail = e.detail;
  }
  if (e.value != null) {
    if (typeof e.value !== "number" || !Number.isFinite(e.value)) return null;
    out.value = Math.round(e.value * 100) / 100;
  }
  return out;
}

export type TelemetrySend = (events: TelemetryEvent[]) => Promise<void>;

export interface Telemetry {
  track(e: TelemetryInput): void;
  /** 지금 큐를 보낸다(보낼 곳이 없으면 그대로 둔다) */
  flush(): Promise<void>;
  /** 보낼 곳(세션) — null 이면 모으기만 */
  setSender(send: TelemetrySend | null): void;
  pending(): readonly TelemetryEvent[];
  dispose(): void;
}

export function createTelemetry(
  opts: { intervalMs?: number; now?: () => number; target?: EventTarget | null } = {},
): Telemetry {
  const now = opts.now ?? Date.now;
  let queue: TelemetryEvent[] = [];
  let send: TelemetrySend | null = null;
  let inFlight: Promise<void> | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;

  const flush = async (): Promise<void> => {
    if (inFlight) await inFlight;
    if (!send || !queue.length) return;
    const batch = queue.slice(0, MAX_BATCH);
    queue = queue.slice(batch.length);
    const s = send;
    inFlight = s(batch)
      .catch(() => {
        // 못 보냈으면 앞에 되돌린다(오래된 것부터 버려 상한 유지)
        queue = [...batch, ...queue].slice(-MAX_QUEUE);
      })
      .finally(() => {
        inFlight = null;
      });
    await inFlight;
    if (queue.length >= MAX_BATCH) await flush();
  };

  const onHide = () => {
    const doc = typeof document !== "undefined" ? document : null;
    if (!doc || doc.visibilityState === "hidden") void flush();
  };
  const target =
    opts.target === undefined ? (typeof window !== "undefined" ? window : null) : opts.target;
  const docTarget = target && typeof document !== "undefined" ? document : null;
  docTarget?.addEventListener("visibilitychange", onHide);
  target?.addEventListener("pagehide", onHide);
  if (opts.intervalMs !== 0)
    timer = setInterval(() => void flush(), opts.intervalMs ?? FLUSH_MS) as ReturnType<
      typeof setInterval
    >;

  return {
    track(e) {
      const ev = sanitize(e, now);
      if (!ev) return;
      queue.push(ev);
      if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
      if (queue.length >= MAX_BATCH) void flush();
    },
    flush,
    setSender(s) {
      send = s;
      if (s && queue.length) void flush();
    },
    pending: () => queue,
    dispose() {
      if (timer) clearInterval(timer);
      docTarget?.removeEventListener("visibilitychange", onHide);
      target?.removeEventListener("pagehide", onHide);
    },
  };
}

/** 셸 전체가 쓰는 하나(화면·공용 컴포넌트는 track 만 부른다 — 보낼 곳은 세션이 생기면 AppLayout 이 붙인다) */
export const telemetry: Telemetry = createTelemetry();

export const track = (e: TelemetryInput) => telemetry.track(e);

/** 수열의 백분위(가까운 순위) — perf 요약(fps p5 · 틱 p95) */
export function percentile(xs: readonly number[], p: number): number | null {
  const v = xs.filter((x) => Number.isFinite(x)).sort((a, b) => a - b);
  if (!v.length) return null;
  const i = Math.min(v.length - 1, Math.max(0, Math.ceil((p / 100) * v.length) - 1));
  return v[i]!;
}
